import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../api.js';
import { useSetupState } from '../lib/useSetupState.js';
import { HelpDrawer, helpSectionForPath } from './HelpDrawer.js';
import { Logo } from './ui.js';

/** Primary navigation — icon paths are 24×24 stroke (Lucide-style). */
const navItems: { to: string; label: string; end?: boolean; icon: string }[] = [
  {
    to: '/',
    label: 'Dashboard',
    end: true,
    icon: 'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z',
  },
  {
    to: '/files',
    label: 'Files',
    icon: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8',
  },
  { to: '/compare', label: 'Compare', icon: 'M8 3L4 7l4 4M4 7h16M16 21l4-4-4-4M20 17H4' },
  { to: '/audit', label: 'Audit', icon: 'M3 3v18h18M7 16v-5M12 16V8M17 16v-9' },
  { to: '/chargebacks', label: 'Chargebacks', icon: 'M9 14L4 9l5-5M4 9h11a5 5 0 010 10h-3' },
  { to: '/ask', label: 'Ask AI', icon: 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z' },
  {
    to: '/docs',
    label: 'Docs',
    icon: 'M4 19.5A2.5 2.5 0 016.5 17H20M4 19.5A2.5 2.5 0 006.5 22H20V2H6.5A2.5 2.5 0 004 4.5z',
  },
];

const ICON_PROFILE = 'M19 21v-2a4 4 0 00-4-4H9a4 4 0 00-4 4v2M16 7a4 4 0 11-8 0 4 4 0 018 0z';
const ICON_WORKSPACES = 'M12 2 2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5';
const ICON_RATES =
  'M19 5L5 19M6.5 6.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM20.5 17.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z';
const ICON_SETUP = 'M12 2l2.2 6.3L20 10.5l-5.8 2.2L12 19l-2.2-6.3L4 10.5l5.8-2.2L12 2z';

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition ${
    isActive
      ? 'bg-emerald-600 text-white shadow-sm'
      : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-white'
  }`;

const menuItem =
  'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition hover:bg-neutral-100 dark:hover:bg-neutral-800';

/**
 * The app's top navigation bar — logo, primary nav, and the account menu.
 * Rendered once by `Layout`, so it's identical on every page.
 */
export function TopNav() {
  const { signOut, entitlements, workspacesEnabled } = useAuth();
  const setup = useSetupState();
  const needsFiles = setup.loaded && !setup.uploaded; // hasn't uploaded a statement
  const needsRates = setup.loaded && !setup.rated; // hasn't set contracted rates
  const navigate = useNavigate();
  const location = useLocation();
  // Disputes lives as a tab under Chargebacks (gated there), so the primary nav is
  // a fixed list.
  const items = navItems;
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [navOpen, setNavOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  const toggleTheme = () => {
    const next = !dark;
    document.documentElement.classList.toggle('dark', next);
    setDark(next);
  };

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setNavOpen(false);
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const signOutAndGo = () => {
    signOut();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-30 border-b border-neutral-200/70 bg-white/80 backdrop-blur-xl dark:border-neutral-800/70 dark:bg-neutral-900/80">
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <div className="flex items-center gap-1.5">
          {/* Mobile menu */}
          <div className="relative md:hidden" ref={navRef}>
            <button
              type="button"
              onClick={() => setNavOpen((o) => !o)}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
              aria-label="Menu"
            >
              <Icon path="M4 6h16M4 12h16M4 18h16" />
            </button>
            {navOpen && (
              <div className="absolute left-0 z-40 mt-2 w-56 rounded-xl border border-neutral-200 bg-white p-1.5 shadow-xl dark:border-neutral-700 dark:bg-neutral-900">
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setNavOpen(false)}
                    className={({ isActive }) =>
                      `relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition ${
                        isActive
                          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                          : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
                      }`
                    }
                  >
                    <Icon path={item.icon} size={16} rounded />
                    {item.label}
                    {item.to === '/files' && needsFiles && <Bubble />}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
          <Link to="/" className="shrink-0">
            <Logo />
          </Link>
        </div>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 md:flex">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={(s) => `relative ${navLinkClass(s)}`}
            >
              <Icon path={item.icon} size={16} rounded />
              {item.label}
              {item.to === '/files' && needsFiles && <Bubble />}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1.5">
          {/* Help */}
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            aria-label="Help & guide"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800"
          >
            <Icon
              path="M12 22a10 10 0 100-20 10 10 0 000 20zM9.2 9a3 3 0 015.6 1.2c0 2-2.8 2.6-2.8 2.6M12 17h.01"
              size={20}
              rounded
            />
          </button>

          {/* Account menu */}
          <div className="relative" ref={userRef}>
            <button
              type="button"
              onClick={() => setUserOpen((o) => !o)}
              className="relative flex items-center gap-1.5 rounded-full border border-neutral-200 py-1 pl-1 pr-2 transition hover:bg-neutral-100 dark:border-neutral-700 dark:hover:bg-neutral-800"
              aria-label="Account menu"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-600 text-white">
                <Icon path={ICON_PROFILE} size={18} rounded />
              </span>
              <Icon path="M7 10l5 5 5-5" size={16} className="text-neutral-400" />
              {needsRates && <Bubble />}
            </button>
            {userOpen && (
              <div className="absolute right-0 z-40 mt-2 w-52 rounded-xl border border-neutral-200 bg-white p-1.5 shadow-xl dark:border-neutral-700 dark:bg-neutral-900">
                <Link
                  to="/rates"
                  onClick={() => setUserOpen(false)}
                  className={`${menuItem} relative`}
                >
                  <Icon path={ICON_RATES} size={16} rounded />
                  Rates
                  {needsRates && <Bubble />}
                </Link>
                <Link to="/carrier-setup" onClick={() => setUserOpen(false)} className={menuItem}>
                  <Icon path={ICON_SETUP} size={16} rounded />
                  Set up a carrier
                </Link>
                {/* Workspace management — only for accounts with the feature on. */}
                {workspacesEnabled && (
                  <Link to="/workspaces" onClick={() => setUserOpen(false)} className={menuItem}>
                    <Icon path={ICON_WORKSPACES} size={16} rounded />
                    Workspaces
                  </Link>
                )}
                <Link to="/profile" onClick={() => setUserOpen(false)} className={menuItem}>
                  <Icon path={ICON_PROFILE} size={16} rounded />
                  Profile &amp; billing
                </Link>
                <Link to="/activity" onClick={() => setUserOpen(false)} className={menuItem}>
                  <Icon
                    path="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8"
                    size={16}
                    rounded
                  />
                  Activity log
                </Link>
                {entitlements.medicareLifecycle && (
                  <Link to="/medicare" onClick={() => setUserOpen(false)} className={menuItem}>
                    <Icon path="M22 12h-4l-3 9L9 3l-3 9H2" size={16} rounded />
                    Medicare lifecycle
                  </Link>
                )}
                {entitlements.bookValue && (
                  <Link to="/book-value" onClick={() => setUserOpen(false)} className={menuItem}>
                    <Icon path="M3 3v18h18M18 17V9M13 17V5M8 17v-3" size={16} rounded />
                    Book value
                  </Link>
                )}
                {entitlements.tax1099 && (
                  <Link to="/tax-1099" onClick={() => setUserOpen(false)} className={menuItem}>
                    <Icon
                      path="M9 7h6M9 11h6M9 15h4M5 3h14a1 1 0 011 1v17l-3-2-2 2-2-2-2 2-2-2-3 2V4a1 1 0 011-1z"
                      size={16}
                      rounded
                    />
                    1099 reconciliation
                  </Link>
                )}
                <button type="button" onClick={toggleTheme} className={menuItem}>
                  {dark ? (
                    <Icon
                      path="M12 3v2M12 19v2M5 12H3M21 12h-2M6 6l1.5 1.5M16.5 16.5L18 18M6 18l1.5-1.5M16.5 7.5L18 6M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      size={16}
                      rounded
                    />
                  ) : (
                    <Icon path="M21 12.8A9 9 0 1111.2 3 7 7 0 0021 12.8z" size={16} rounded />
                  )}
                  {dark ? 'Light mode' : 'Dark mode'}
                </button>
                <div className="my-1 border-t border-neutral-100 dark:border-neutral-800" />
                <button
                  type="button"
                  onClick={signOutAndGo}
                  className={`${menuItem} text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/30`}
                >
                  <Icon
                    path="M15 17l5-5-5-5M20 12H9M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"
                    size={16}
                    rounded
                  />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <HelpDrawer
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        section={helpSectionForPath(location.pathname)}
      />
    </header>
  );
}

/** A small pulsing "do this next" callout dot, anchored to a nav item's corner. */
function Bubble() {
  return (
    <span className="absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5" aria-hidden="true">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
    </span>
  );
}

function Icon({
  path,
  size = 20,
  className = '',
  rounded = false,
}: {
  path: string;
  size?: number;
  className?: string;
  rounded?: boolean;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={`shrink-0 ${className}`}
    >
      <path
        d={path}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap={rounded ? 'round' : 'butt'}
        strokeLinejoin={rounded ? 'round' : 'miter'}
      />
    </svg>
  );
}
